import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

import { AnnouncementBanner } from '@/components/AnnouncementBanner';
import { FellowMentorBanner } from '@/components/FellowMentorBanner';
import { GlobalBannerErrorBoundary } from '@/components/GlobalBannerErrorBoundary';
import { View } from '@/components/ui/view';
import { GLOBAL_BANNER_STACK, GLOBAL_BANNER_SURFACE } from '@/constants/banners';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useFellowMentorBanner } from '@/hooks/useFellowMentorBanner';
import { useAnnouncementSlotStore } from '@/services/stores/announcementSlotStore';
import { isEHRSession } from '@/util/smartLaunch';

/**
 * The one place app-level banners render, mounted once in the secure layout.
 *
 * The promo and a screen's announcement are both on screen when both are
 * eligible, in the tier order `banners.md` sets out: the announcement above the
 * promo. Nothing is hidden behind anything, so a reader never has to discover a
 * second banner to find out it was there.
 *
 * The announcement goes on top because it cannot be dismissed here. The promo
 * can, and a card that can be cleared is the one that should sit under the card
 * that cannot, so dismissing it shortens the stack from the bottom instead of
 * pulling a banner out from under one that stays.
 *
 * The announcement card holds one of several announcements, crossfading between
 * them on a shared clock. Only its contents change — the card itself stays put,
 * so the stack above does not resize and the promo below does not move.
 *
 * Announcements belong to particular screens, so those screens register
 * themselves rather than rendering their own banner. Rendering here while
 * eligibility is declared there is what keeps a screen from having to know about
 * the promo, and keeps this from having to know which routes carry
 * announcements.
 *
 * Nothing in this slot renders for a user held on the account-details gate. Every
 * call to action here names a secure route, and that gate is the one park with no
 * browsing allowed at all, so the promo's button is a dead end rather than an
 * offer. It also competes for attention with the single task the screen exists to
 * ask for. The NPI park is deliberately not treated the same way: those users do
 * browse the public routes on web, where the promo is both reachable and wanted.
 *
 * The impersonation notice is separate and stays up regardless, since a system
 * banner reports a state the whole app is in rather than making an offer.
 */
function GlobalBannerSlotContent() {
  const { needsAccountDetailsConfirmation } = useAuthStatus();

  const { isShowing: isPromoShowing } = useFellowMentorBanner();

  const { config, handleCtaPress, isAnnouncementReady, announcementSignature } = useAnnouncements();

  const hasAnnouncementSurface = useAnnouncementSlotStore((state) => state.waitingCount > 0);

  const hasAnnouncement = isAnnouncementReady && hasAnnouncementSurface;

  if (
    !isWeb ||
    needsAccountDetailsConfirmation ||
    isEHRSession() ||
    (!isPromoShowing && !hasAnnouncement)
  ) {
    return null;
  }

  return (
    <View className={GLOBAL_BANNER_SURFACE}>
      <View className={GLOBAL_BANNER_STACK}>
        {hasAnnouncement ? (
          <AnnouncementBanner
            isEnabled
            contentKey={announcementSignature}
            variant={config?.variant}
            title={config?.title}
            message={config?.message}
            emoji={config?.emoji}
            showIcon={config?.showIcon}
            ctaLabel={config?.ctaLabel}
            ctaUrl={config?.ctaUrl}
            ctaAction={config?.ctaAction}
            onPress={handleCtaPress}
            buttonClassName="px-5"
          />
        ) : null}

        {isPromoShowing ? <FellowMentorBanner /> : null}
      </View>
    </View>
  );
}

/**
 * The exported slot, wrapped so the boundary cannot be forgotten at the mount
 * site. This renders on every authenticated screen, so an unguarded throw here
 * blanks the app rather than the strip.
 */
export function GlobalBannerSlot() {
  return (
    <GlobalBannerErrorBoundary surface="global-banner-slot">
      <GlobalBannerSlotContent />
    </GlobalBannerErrorBoundary>
  );
}
